import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { LayoutGrid, Croissant, Beef, Droplet, Utensils, Apple } from 'lucide-react';
import milkCartonIcon from '@/assets/icons/milk-carton.png';
import milkCartonHoverIcon from '@/assets/icons/milk-carton-hover.png';
import glassBottleIcon from '@/assets/icons/glass-bottle.png';
import glassBottleHoverIcon from '@/assets/icons/glass-bottle-hover.png';
import spreadsBowlIcon from '@/assets/icons/spreads-bowl.png';
import spreadsBowlHoverIcon from '@/assets/icons/spreads-bowl-hover.png';

// Industry categories mapping
const industryCategories = [
  { 
    id: 'all', 
    icon: LayoutGrid,
    isImage: false,
    label: { el: 'Όλα', en: 'All' }
  },
  { 
    id: 'dairy', 
    icon: milkCartonIcon,
    hoverIcon: milkCartonHoverIcon,
    isImage: true,
    label: { el: 'Γαλακτοκομικά', en: 'Dairy' }
  },
  { 
    id: 'beverage', 
    icon: glassBottleIcon,
    hoverIcon: glassBottleHoverIcon,
    isImage: true,
    label: { el: 'Ποτά', en: 'Beverages' }
  },
  { 
    id: 'bakery', 
    icon: Croissant,
    isImage: false,
    label: { el: 'Αρτοσκευάσματα', en: 'Bakery' }
  },
  { 
    id: 'meat-fish-eggs', 
    icon: Beef,
    isImage: false,
    label: { el: 'Κρέας & Ψάρι', en: 'Meat & Fish' }
  },
  { 
    id: 'oils-fats', 
    icon: Droplet,
    isImage: false,
    label: { el: 'Έλαια & Λίπη', en: 'Oils & Fats' }
  },
  { 
    id: 'spreads', 
    icon: spreadsBowlIcon,
    hoverIcon: spreadsBowlHoverIcon,
    isImage: true,
    label: { el: 'Αλείμματα', en: 'Spreads' }
  },
  { 
    id: 'fruit-vegetables', 
    icon: Apple,
    isImage: false,
    label: { el: 'Φρούτα & Λαχανικά', en: 'Fruit & Vegetables' }
  },
  { 
    id: 'ready-meals', 
    icon: Utensils,
    isImage: false,
    label: { el: 'Έτοιμα Γεύματα', en: 'Ready Meals' }
  }
];

const projects = [
  {
    title: 'Vivartia Group & Δέλτα',
    client: 'Vivartia Group',
    year: '2008-2020',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Ολοκληρωμένες λύσεις για εργοστάσιο γάλακτος και UHT',
    scope: ['Εργοστάσιο φρέσκου γάλακτος', 'Εργοστάσιο γάλακτος UHT', 'Συστήματα επεξεργασίας'],
    capacity: 'Μεγάλης κλίμακας παραγωγή'
  },
  {
    title: 'ΕΒΓΑ - Εργοστάσιο Παγωτού & Χυμών',
    client: 'ΕΒΓΑ',
    year: '2005-2010',
    industry: 'Παγωτό & Ποτά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εργοστάσιο παραγωγής παγωτού και εργοστάσιο χυμού',
    scope: ['Γραμμή παραγωγής παγωτού', 'Μονάδα χυμών', 'Ψυκτικές εγκαταστάσεις'],
    capacity: 'Βιομηχανική κλίμακα'
  },
  {
    title: 'Nestle (Δέλτα Παγωτό)',
    client: 'Nestle',
    year: '1998-2007',
    industry: 'Παγωτό',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εργοστάσια παραγωγής παγωτού σε Ελλάδα, Ρουμανία, Βουλγαρία, Σερβία',
    scope: ['Ρουμανία 1998-2000-2007', 'Βουλγαρία & Σερβία', 'Πλήρης εξοπλισμός παραγωγής'],
    capacity: 'Πολλαπλές εγκαταστάσεις'
  },
  {
    title: 'Δωδώνη - Ολοκληρωμένες Λύσεις',
    client: 'Δωδώνη',
    year: '2008-2020',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Πλήρης γραμμή γαλακτοκομικών προϊόντων και τυροκομίας',
    scope: ['Εργοστάσιο γαλακτοκομικών', 'Γιαούρτι', 'Σκληρό και μαλακό τυρί', 'Φρέσκο γάλα', 'Φέτα'],
    capacity: 'Μεγάλης κλίμακας'
  },
  {
    title: 'Αυτοματισμός Πλύσης (CIP) Εργοστασίου - Δωδώνη Α.Ε.',
    client: 'Δωδώνη',
    year: '2020',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Αυτοματισμός συστήματος πλύσης CIP εργοστασίου',
    scope: ['Σύστημα CIP', 'Αυτοματισμός παραγωγής'],
    capacity: 'Βιομηχανική κλίμακα'
  },
  {
    title: 'Φάγε - Εργοστάσιο Φέτας',
    client: 'Φάγε',
    year: '2003',
    industry: 'Τυροκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Νέο εργοστάσιο παραγωγής φέτας και τυριού Κατίκι',
    scope: ['Γραμμή παραγωγής φέτας', 'Εξοπλισμός τυροκομίας'],
    capacity: 'Υψηλής δυναμικότητας'
  },
  {
    title: 'Εγκατάσταση Αναλυτή Γάλακτος ProFoss - ΦΑΓΕ',
    client: 'ΦΑΓΕ',
    year: '2023',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτή γάλακτος και γαλακτοκομικών προϊόντων ProFoss',
    scope: ['Ποιοτικός έλεγχος', 'Εργαστηριακός εξοπλισμός'],
    capacity: 'Υψηλής ακρίβειας'
  },
  {
    title: 'Dodoni Cyprus - Halloumi Plant',
    client: 'Dodoni Cyprus',
    year: '2015-Σήμερα',
    industry: 'Τυροκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εργοστάσιο παραγωγής Halloumi στην Κύπρο',
    scope: ['Εξειδικευμένος εξοπλισμός Halloumi', 'Συστήματα τυροκομίας'],
    capacity: 'Εξαγωγική παραγωγή'
  },
  {
    title: 'Αναλυτής Γάλακτος ProFoss - ΚΡΙ ΚΡΙ Α.Ε.',
    client: 'ΚΡΙ ΚΡΙ',
    year: '2022',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτή γάλακτος και γαλακτοκομικών προϊόντων',
    scope: ['Ανάλυση σύστασης γάλακτος', 'Ποιοτικός έλεγχος'],
    capacity: 'Ταχεία ανάλυση'
  },
  {
    title: 'Αναλυτής Γάλακτος ProFoss - ΝΕΟΓΑΛ',
    client: 'Βιομηχανία Γάλακτος Δράμας ΝΕΟΓΑΛ',
    year: '2022',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτή γάλακτος για ποιοτικό έλεγχο',
    scope: ['Ποιοτικός έλεγχος', 'Εργαστηριακή ανάλυση'],
    capacity: 'Υψηλής ακρίβειας'
  },
  {
    title: 'MilkoScan - ΕΛΛΗΝΙΚΑ ΓΑΛΑΚΤΟΜΕΙΑ Α.Ε.',
    client: 'ΕΛΛΗΝΙΚΑ ΓΑΛΑΚΤΟΜΕΙΑ',
    year: '2021',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτή γάλακτος MilkoScan',
    scope: ['Ανάλυση γάλακτος', 'Ποιοτικός έλεγχος'],
    capacity: 'Βιομηχανική κλίμακα'
  },
  {
    title: 'CombiFoss & BactoScan - ΕΛΓΟ ΔΗΜΗΤΡΑ',
    client: 'ΕΛΓΟ ΔΗΜΗΤΡΑ',
    year: '2023',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτών γάλακτος για μικροβιολογικό έλεγχο',
    scope: ['Μικροβιολογικός έλεγχος', 'Ποιοτική ανάλυση'],
    capacity: 'Ερευνητική κλίμακα'
  },
  {
    title: 'Τεχνολογική Αναβάθμιση - ΗΠΕΙΡΟΣ ΑΒΕΕ',
    client: 'ΗΠΕΙΡΟΣ',
    year: '2024',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Έργο τεχνολογικής αναβάθμισης εγκαταστάσεων στην Άρτα',
    scope: ['Αναβάθμιση εξοπλισμού', 'Αυτοματισμοί παραγωγής'],
    capacity: 'Μεγάλης κλίμακας'
  },
  {
    title: 'Γραμμές Επεξεργασίας Γιαουρτιού - Ελληνικά Γαλακτοκομεία',
    client: 'ΕΛΛΗΝΙΚΑ ΓΑΛΑΚΤΟΜΕΙΑ',
    year: '2023',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση γραμμών επεξεργασίας γιαουρτιού',
    scope: ['Γραμμές παραγωγής', 'Επεξεργασία γιαουρτιού'],
    capacity: 'Υψηλής δυναμικότητας'
  },
  {
    title: 'Αναβάθμιση Τυροκομείου - Greek Family Farm',
    client: 'Greek Family Farm',
    year: '2023',
    industry: 'Τυροκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Αναβάθμιση εγκαταστάσεων τυροκομείου',
    scope: ['Εξοπλισμός τυροκομίας', 'Μοντερνοποίηση παραγωγής'],
    capacity: 'Μεσαίας κλίμακας'
  },
  {
    title: 'Νέο Τυροκομείο - ΕΑΣ Νάξου',
    client: 'ΕΑΣ Νάξου',
    year: '2024',
    industry: 'Τυροκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Εγκατάσταση νέου τυροκομείου',
    scope: ['Πλήρης εξοπλισμός', 'Παραγωγική διαδικασία'],
    capacity: 'Σύγχρονες εγκαταστάσεις'
  },
  {
    title: 'Friesland Campina Πάτρα',
    client: 'Friesland Campina',
    year: '2010-2015',
    industry: 'Γαλακτοκομικά',
    category: 'dairy',
    icon: milkCartonIcon,
    isImage: true,
    description: 'Λύσεις επεξεργασίας και συσκευασίας',
    scope: ['Επεξεργασία γάλακτος', 'Συστήματα συσκευασίας'],
    capacity: 'Μεγάλης κλίμακας'
  },
  {
    title: 'Αναλυτής Οίνου OenoFOSS - ΒΙΒΛΙΑ ΧΩΡΑ',
    client: 'Κτήμα ΒΙΒΛΙΑ ΧΩΡΑ',
    year: '2023',
    industry: 'Οινοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Προμήθεια και εγκατάσταση αναλυτή οίνου',
    scope: ['Ανάλυση οίνου', 'Ποιοτικός έλεγχος'],
    capacity: 'Οινοποιείο'
  },
  {
    title: 'WineScan Flex SO2 - SANTO WINES',
    client: 'SANTO WINES',
    year: '2023',
    industry: 'Οινοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτή οίνου με μέτρηση SO2',
    scope: ['Ανάλυση οίνου', 'Μέτρηση θειωδών'],
    capacity: 'Συνεταιριστικό οινοποιείο'
  },
  {
    title: 'WineScan Flex - CAIR',
    client: 'CAIR',
    year: '2022',
    industry: 'Οινοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Προμήθεια και εγκατάσταση συστήματος ανάλυσης οίνου',
    scope: ['Ποιοτική ανάλυση', 'Εργαστηριακός εξοπλισμός'],
    capacity: 'Οινοποιείο'
  },
  {
    title: 'Αναλυτής Οίνου OenoFOSS - ΓΕΡΟΒΑΣΙΛΕΙΟΥ',
    client: 'Κτήμα ΓΕΡΟΒΑΣΙΛΕΙΟΥ',
    year: '2023',
    industry: 'Οινοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εγκατάσταση αναλυτή οίνου για ποιοτικό έλεγχο',
    scope: ['Ανάλυση οίνου', 'Ποιοτικός έλεγχος'],
    capacity: 'Κορυφαίο οινοποιείο'
  },
  {
    title: 'WineScan Auto & WineScan Auto SO2 - ΑΛΦΑ',
    client: 'Κτήμα ΑΛΦΑ',
    year: '2024',
    industry: 'Οινοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εγκατάσταση συστημάτων αυτόματης ανάλυσης οίνου',
    scope: ['Αυτόματη ανάλυση', 'Μέτρηση παραμέτρων οίνου'],
    capacity: 'Σύγχρονο οινοποιείο'
  },
  {
    title: 'WineScan 3 SO2 - ΣΚΟΥΡΑΣ',
    client: 'Κτήμα ΣΚΟΥΡΑΣ',
    year: '2022',
    industry: 'Οινοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Προμήθεια και εγκατάσταση αναλυτή οίνου',
    scope: ['Ανάλυση οίνου', 'Ποιοτικός έλεγχος'],
    capacity: 'Οινοποιείο'
  },
  {
    title: 'Εξοπλισμός Γυάλινης Φιάλης 750ml - ΘΕΟΝΗ',
    client: 'ΘΕΟΝΗ',
    year: '2023',
    industry: 'Νερό',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εγκατάσταση εξοπλισμού συσκευασίας για γυάλινη φιάλη',
    scope: ['Γραμμή συσκευασίας', 'Γυάλινες φιάλες'],
    capacity: 'Υψηλής ποιότητας'
  },
  {
    title: 'Εξοπλισμός Χαρτοκύτιου 500ml - ΘΕΟΝΗ',
    client: 'ΘΕΟΝΗ',
    year: '2023',
    industry: 'Νερό',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Συσκευασία και επισκευασία για χαρτοκύτιο νερού',
    scope: ['Γραμμή συσκευασίας', 'Χαρτοκύτια'],
    capacity: 'Οικολογική συσκευασία'
  },
  {
    title: 'Εξοπλισμός Χαρτοκύτιου 330ml - ΘΕΟΝΗ',
    client: 'ΘΕΟΝΗ',
    year: '2022',
    industry: 'Νερό',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εξοπλισμός συσκευασίας και επισκευασίας για χαρτοκύτιο',
    scope: ['Συσκευασία', 'Επισκευασία'],
    capacity: 'Μικρό μέγεθος'
  },
  {
    title: 'Smart Kegging Machine K1 - CANDIA',
    client: 'Ζυθοποιία CANDIA',
    year: '2024',
    industry: 'Ζυθοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Παροχή Sales & After Sales Service για μηχανή συσκευασίας βαρελιών',
    scope: ['Smart kegging', 'Αυτοματοποίηση'],
    capacity: 'Craft brewery'
  },
  {
    title: 'Smart Kegging Machine K1 - FTELOS',
    client: 'Ζυθοποιία FTELOS',
    year: '2023',
    industry: 'Ζυθοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Προμήθεια και εγκατάσταση μηχανής συσκευασίας βαρελιών',
    scope: ['Kegging machine', 'Συσκευασία βαρελιών'],
    capacity: 'Μικρή ζυθοποιία'
  },
  {
    title: 'Kegging Machine K1 - ΠΗΝΕΙΟΣ',
    client: 'Ζυθοποιία ΠΗΝΕΙΟΣ',
    year: '2023',
    industry: 'Ζυθοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εγκατάσταση μηχανής συσκευασίας βαρελιών',
    scope: ['Συσκευασία', 'Αυτοματισμός'],
    capacity: 'Craft brewery'
  },
  {
    title: 'Spx Self Cleaning Clarifier - 33 BREWERY',
    client: 'Ζυθοποιία 33',
    year: '2022',
    industry: 'Ζυθοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Εγκατάσταση αυτοκαθαριζόμενου διαυγαστήρα',
    scope: ['Διαύγαση μπύρας', 'Αυτόματος καθαρισμός'],
    capacity: 'Μεσαίας κλίμακας'
  },
  {
    title: 'Spx Self Cleaning Clarifier - SEPTEM',
    client: 'Ζυθοποιία SEPTEM',
    year: '2022',
    industry: 'Ζυθοποιία',
    category: 'beverage',
    icon: glassBottleIcon,
    isImage: true,
    description: 'Προμήθεια και εγκατάσταση διαυγαστήρα',
    scope: ['Διαύγαση', 'Ποιοτικός έλεγχος'],
    capacity: 'Craft brewery'
  },
  {
    title: 'FoodScan - Μέγας Ελληνικός και Παραδοσιακός Γύρος',
    client: 'Μέγας Γύρος',
    year: '2023',
    industry: 'Κρέας',
    category: 'meat-fish-eggs',
    icon: Beef,
    isImage: false,
    description: 'Εγκατάσταση in-line αναλυτή κρέατος και κρεατοσκευασμάτων',
    scope: ['Ποιοτικός έλεγχος', 'In-line ανάλυση'],
    capacity: 'Μεγάλης κλίμακας'
  },
  {
    title: 'FoodScan - ΦΑΡΜΑ ΜΗΤΣΟΠΟΥΛΟΣ',
    client: 'ΦΑΡΜΑ ΜΗΤΣΟΠΟΥΛΟΣ',
    year: '2023',
    industry: 'Κρέας',
    category: 'meat-fish-eggs',
    icon: Beef,
    isImage: false,
    description: 'Εγκατάσταση in-line αναλυτή κρέατος',
    scope: ['Ανάλυση κρέατος', 'Ποιοτικός έλεγχος'],
    capacity: 'Βιομηχανική παραγωγή'
  },
  {
    title: 'MeatScan - ΚΑΣΙΔΗΣ Α.Ε',
    client: 'ΚΑΣΙΔΗΣ',
    year: '2022',
    industry: 'Κρέας',
    category: 'meat-fish-eggs',
    icon: Beef,
    isImage: false,
    description: 'Εγκατάσταση in-line αναλυτή κρεατοσκευασμάτων',
    scope: ['Ανάλυση σύστασης', 'Ποιοτικός έλεγχος'],
    capacity: 'Κρεατοσκευάσματα'
  },
  {
    title: 'FoodScan 2 - ELVIDA FOODS',
    client: 'ELVIDA FOODS',
    year: '2023',
    industry: 'Κρέας',
    category: 'meat-fish-eggs',
    icon: Beef,
    isImage: false,
    description: 'Εγκατάσταση αναλυτή κρέατος και κρεατοσκευασμάτων',
    scope: ['Εργαστηριακή ανάλυση', 'Ποιοτικός έλεγχος'],
    capacity: 'Υψηλής ακρίβειας'
  },
  {
    title: 'MeatMaster II - ELVIDA FOODS',
    client: 'ELVIDA FOODS',
    year: '2022',
    industry: 'Κρέας',
    category: 'meat-fish-eggs',
    icon: Beef,
    isImage: false,
    description: 'Εγκατάσταση in-line αναλυτή κρέατος',
    scope: ['In-line ανάλυση', 'Αυτόματος έλεγχος'],
    capacity: 'Βιομηχανική κλίμακα'
  }
];

const Experience = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Calculate which categories have projects
  const categoriesWithProjects = new Set(projects.map(p => p.category));
  
  // Filter categories to only show those with projects (plus "all")
  const availableCategories = industryCategories.filter(
    cat => cat.id === 'all' || categoriesWithProjects.has(cat.id)
  );
  
  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Εμπειρία', 'Experience')}
        description={t('60+ χρόνια εμπειρίας σε έργα επεξεργασίας και συσκευασίας τροφίμων στην Ελλάδα και τα Βαλκάνια.', '60+ years of experience in food processing and packaging projects in Greece and the Balkans.')}
        canonical="https://www.tft.gr/company/experience"
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-primary to-primary-light text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-6">{t('Η Εμπειρία μας', 'Our Experience')}</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              {t('Περισσότερα από 60 χρόνια εμπειρίας σε εγκαταστάσεις παγκόσμιου επιπέδου', 'Over 60 years of experience in world-class installations')}
            </p>
          </div>
        </section>

        {/* Projects Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">{t('Επιλεγμένα Έργα', 'Selected Projects')}</h2>
              
              {/* Category Filter */}
              <div className="flex justify-center mb-8">
                <ToggleGroup 
                  type="single" 
                  value={selectedCategory}
                  onValueChange={(value) => value && setSelectedCategory(value)}
                  className="bg-muted/50 p-2 rounded-lg"
                >
                  {availableCategories.map((category) => {
                    const IconComponent = category.icon;
                    const isSelected = selectedCategory === category.id;
                    const displayIcon = isSelected && category.hoverIcon ? category.hoverIcon : IconComponent;
                    
                    return (
                      <ToggleGroupItem
                        key={category.id}
                        value={category.id}
                        className="flex items-center gap-2 px-6 py-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                      >
                        {category.isImage ? (
                          <img 
                            src={displayIcon as string} 
                            alt={t(category.label.el, category.label.en)} 
                            className="h-5 w-5"
                          />
                        ) : IconComponent ? (
                          <IconComponent className="h-5 w-5" />
                        ) : null}
                        <span>{t(category.label.el, category.label.en)}</span>
                      </ToggleGroupItem>
                    );
                  })}
                </ToggleGroup>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => {
                const IconComponent = project.icon as any;
                const isImage = project.isImage;
                
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <Badge variant="secondary" className="mb-2">{project.year}</Badge>
                          <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">{project.client}</p>
                        </div>
                        <div className="p-3 bg-primary/10 rounded-xl">
                          {isImage ? (
                            <img 
                              src={IconComponent} 
                              alt={project.title} 
                              className="h-8 w-8"
                            />
                          ) : IconComponent ? (
                            <IconComponent className="h-8 w-8 text-primary" />
                          ) : null}
                        </div>
                      </div>
                      
                      <Badge className="mb-3">{project.industry}</Badge>
                      
                      <p className="text-muted-foreground mb-4">{project.description}</p>
                      
                      <div className="space-y-2">
                        <h4 className="font-semibold text-sm">{t('Πεδίο Εφαρμογής:', 'Scope:')}</h4>
                        <ul className="space-y-1">
                          {project.scope.map((item, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start">
                              <span className="mr-2">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t">
                        <span className="text-sm font-medium">{project.capacity}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-primary mb-2">60+</div>
                <div className="text-muted-foreground">{t('Χρόνια Εμπειρίας', 'Years of Experience')}</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-primary mb-2">200+</div>
                <div className="text-muted-foreground">{t('Ολοκληρωμένα Έργα', 'Completed Projects')}</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-primary mb-2">150+</div>
                <div className="text-muted-foreground">{t('Πελάτες', 'Clients')}</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-primary mb-2">30+</div>
                <div className="text-muted-foreground">{t('Συνεργάτες', 'Partners')}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12">{t('Τι λένε οι πελάτες μας', 'What Our Clients Say')}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg italic mb-6">
                    "{t('Η συνεργασία μας με την TFT ήταν εξαιρετική. Η τεχνογνωσία τους και η αφοσίωσή τους στην ποιότητα ήταν καθοριστικές για την επιτυχία του έργου μας.', 'Our collaboration with TFT was exceptional. Their expertise and commitment to quality were crucial to the success of our project.')}"
                  </p>
                  <div className="font-semibold">{t('Τεχνικός Διευθυντής', 'Technical Director')}</div>
                  <div className="text-sm text-muted-foreground">{t('Μεγάλη Γαλακτοβιομηχανία', 'Major Dairy Company')}</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-8">
                  <p className="text-lg italic mb-6">
                    "{t('Η TFT μας προσέφερε μια ολοκληρωμένη λύση που ξεπέρασε τις προσδοκίες μας. Η υποστήριξη μετά την εγκατάσταση είναι εξίσου εντυπωσιακή.', 'TFT provided us with a complete solution that exceeded our expectations. The after-installation support is equally impressive.')}"
                  </p>
                  <div className="font-semibold">{t('Διευθύνων Σύμβουλος', 'CEO')}</div>
                  <div className="text-sm text-muted-foreground">{t('Οινοποιείο', 'Winery')}</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Experience;